import { useMemo } from "react";
import { useOutletContext, useParams } from "react-router";
import type { TalkingPoint } from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useHiddenMeetingPrepCards } from "@acto/taskpane/core/data-access/meeting-prep-card-hides";
import { useMeetingPrepLearnedDefaults } from "@acto/taskpane/core/data-access/meeting-prep-learned-defaults";
import { useProductExclusions } from "@acto/taskpane/core/data-access/product-exclusions";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { useMeetingPrepEntry } from "@acto/taskpane/field-sales-2/data-access/meeting-prep/useMeetingPrepEntry";
import { Headline } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { ShellBackButton } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar";
import { talkingPointLabel } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/cardLabel";
import { hideDurationLabel } from "@acto/taskpane/field-sales-2/components/patterns/product-hide/hideDurationLabel";
import {
  ProfileEmptyState,
  ProfileHeader,
  ProfileMeta,
  ProfileSection,
  ProfileSections,
  useLearnedDefaultEntries,
  type ProfileEntry,
} from "@acto/taskpane/field-sales-2/components/patterns/ProfileSection";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";

export function MeetingPrepProfile() {
  const { id: clientId = "" } = useParams<{ id: string }>();
  const { customer, isPending: isCustomerPending } = useOutletContext<CustomerOutletContext>();
  const t = useTranslations("fieldSalesTwo.meetingPrepProfile");
  const tCardHide = useTranslations("fieldSalesTwo.genuiMeetingPrep.cardHide");
  const tProductHide = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const tLearnedDefault = useTranslations("fieldSalesTwo.genuiMeetingPrep.learnedDefault");
  const { locale } = useLocale();

  const { sessionId } = useMeetingPrepEntry(customer?.externalReference);
  const { data: hides, isPending: isHidesPending, error: hidesError } = useHiddenMeetingPrepCards(clientId, sessionId);
  const { data: exclusions, isPending: isExclusionsPending, error: exclusionsError } = useProductExclusions(clientId);
  const {
    data: learnedDefaults,
    isPending: isLearnedDefaultsPending,
    error: learnedDefaultsError,
  } = useMeetingPrepLearnedDefaults();
  const learnedDefaultEntries = useLearnedDefaultEntries(learnedDefaults);

  const hiddenCards = useMemo<ProfileEntry[]>(() => {
    if (!hides) return [];

    return hides.map((hide) => ({
      id: hide.id,
      title: talkingPointLabel({ type: hide.cardType as TalkingPoint["type"], cardKey: hide.cardKey }, tCardHide),
      meta: t("untilNextPrep"),
    }));
  }, [hides, t, tCardHide]);

  const hiddenProducts = useMemo<ProfileEntry[]>(
    () =>
      (exclusions ?? []).map((exclusion) => ({
        id: exclusion.id,
        title: exclusion.name(locale) ?? exclusion.productId,
        meta: hideDurationLabel(exclusion.duration(), tProductHide, locale),
      })),
    [exclusions, locale, tProductHide],
  );

  const isEmpty = hiddenCards.length === 0 && hiddenProducts.length === 0 && learnedDefaultEntries.length === 0;

  return (
    <>
      <ShellSlotContent slot="topBarLeading" id="meetingPrepProfileBack">
        <ShellBackButton />
      </ShellSlotContent>
      <PageLayout
        hPadding="card"
        isLoading={isCustomerPending || isHidesPending || isExclusionsPending || isLearnedDefaultsPending}
        isError={Boolean(hidesError || exclusionsError || learnedDefaultsError)}
        isEmpty={isEmpty}
        emptyState={<ProfileEmptyState caption={customer?.name} title={t("title")} subtitle={t("emptySubtitle")} />}
      >
        <ProfileHeader>
          {customer?.name && <ProfileMeta>{customer.name}</ProfileMeta>}
          <Headline>{t("title")}</Headline>
        </ProfileHeader>
        <ProfileSections>
          <ProfileSection label={t("hiddenCards")} entries={hiddenCards} />
          <ProfileSection label={t("hiddenProducts")} entries={hiddenProducts} />
          <ProfileSection label={tLearnedDefault("title")} entries={learnedDefaultEntries} />
        </ProfileSections>
      </PageLayout>
    </>
  );
}
